import Phaser from "phaser";
import PaymentManager from "../game/PaymentManager";
import XAuth from "../game/XAuth";
import SoundManager from "../game/SoundManager";
import { addMuteButton } from "../game/MuteButton";
import { FONT_SIZE, fontStyle } from "../game/FontScale";
import { fitText, isNarrow } from "../game/view/layout";

const EDGE_MARGIN = 16; // total horizontal margin for centred lines
const PULSE_SCALE = 1.05; // winner-name pulse peak scale
const VEST_BAR_H = 16;
const VEST_GAP = 20; // gap between bars (wide) / rows (narrow)
const VEST_LABEL_GAP = 4; // label-to-bar spacing

/**
 * EpochEndScene — ceremony screen shown when an epoch concludes.
 *
 * Displays: winner announcement, prize amount, vesting schedule,
 * player's final rank/percentile, epoch recap stats, and a
 * "NEW EPOCH STARTS NOW" button.
 *
 * Scene data: { epoch } — the completed epoch object from API
 */

class EpochEndScene extends Phaser.Scene {
  constructor() {
    super({ key: "EpochEndScene" });
  }

  create(data) {
    this.cameras.main.setBackgroundColor("#0a0a0a");
    this.epoch = data?.epoch || {};

    const W = this.scale.width;
    const H = this.scale.height;
    this._W = W;
    const maxW = W - EDGE_MARGIN;

    // Gold particle rain (if ParticleManager is available)
    this._startGoldRain();

    // ── "EPOCH COMPLETE" header ────────────────────────────────
    const epochNum = this.epoch.id || "?";
    fitText(
      this.add
        .text(
          W / 2,
          40,
          `EPOCH #${epochNum} COMPLETE`,
          fontStyle(FONT_SIZE.TITLE, "#FFD700", {
            stroke: "#8B6914",
            strokeThickness: 3,
          }),
        )
        .setOrigin(0.5),
      maxW,
    );

    // Flash animation
    const flash = this.add.graphics();
    flash.fillStyle(0xffd700, 0.15);
    flash.fillRect(0, 0, W, H);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 1500,
      ease: "Power2",
    });

    // ── Winner announcement ────────────────────────────────────
    const winnerHandle = this.epoch.winnerHandle || "UNKNOWN";
    const winnerScore = (this.epoch.winnerScore || 0).toLocaleString();
    const prizeUsd = (this.epoch.prizePoolUsd || 0).toFixed(2);

    this.add
      .text(W / 2, 90, "WINNER", fontStyle(FONT_SIZE.BODY, "#999999"))
      .setOrigin(0.5);

    const winnerText = fitText(
      this.add
        .text(W / 2, 120, winnerHandle, fontStyle(FONT_SIZE.HEADING, "#39FF14"))
        .setOrigin(0.5),
      maxW / PULSE_SCALE,
    );

    // Winner name pulse
    this.tweens.add({
      targets: winnerText,
      scaleX: { from: 1, to: PULSE_SCALE },
      scaleY: { from: 1, to: PULSE_SCALE },
      duration: 800,
      yoyo: true,
      repeat: 3,
    });

    fitText(
      this.add
        .text(
          W / 2,
          150,
          `${winnerScore} PTS`,
          fontStyle(FONT_SIZE.SUBHEADING, "#F5E6C8"),
        )
        .setOrigin(0.5),
      maxW,
    );

    // Prize amount
    fitText(
      this.add
        .text(
          W / 2,
          180,
          `PRIZE: $${prizeUsd}`,
          fontStyle(FONT_SIZE.HEADING, "#FFD700"),
        )
        .setOrigin(0.5),
      maxW,
    );

    // ── Vesting schedule ───────────────────────────────────────
    const vestY = 220;
    this.add
      .text(
        W / 2,
        vestY,
        "VESTING SCHEDULE",
        fontStyle(FONT_SIZE.CAPTION, "#666666"),
      )
      .setOrigin(0.5);

    const vestData = [
      { label: "NOW", pct: "40%", color: "#39FF14" },
      { label: "7 DAYS", pct: "30%", color: "#00FFFF" },
      { label: "30 DAYS", pct: "30%", color: "#FF00FF" },
    ];

    const vestBottom = this._drawVesting(vestData, vestY + 14);
    // Push everything below down if the vesting rows grew (narrow stacking)
    const shift = Math.max(0, vestBottom - 262);

    // ── Player's result ────────────────────────────────────────
    const playerY = 290 + shift;
    const xUser = XAuth.getUser();
    const myHandle = xUser ? xUser.handle : null;

    if (myHandle) {
      this._loadPlayerRank(myHandle, playerY);
    } else {
      fitText(
        this.add
          .text(
            W / 2,
            playerY + 20,
            "SIGN IN TO SEE YOUR RANK",
            fontStyle(FONT_SIZE.CAPTION, "#999999"),
          )
          .setOrigin(0.5),
        maxW,
      );
    }

    // ── Epoch recap ────────────────────────────────────────────
    const recapY = 390 + shift;
    this.add
      .text(W / 2, recapY, "EPOCH RECAP", fontStyle(FONT_SIZE.BODY, "#666666"))
      .setOrigin(0.5);

    const totalPlayers = this.epoch.totalPlayers || "?";
    const totalLevels = this.epoch.totalLevelsCompleted || "?";
    const totalRevenue = (this.epoch.totalRevenue || 0).toFixed(2);

    const recapItems = [
      `${totalPlayers} PLAYERS`,
      `${totalLevels} LEVELS COMPLETED`,
      `$${totalRevenue} TOTAL REVENUE`,
    ];

    recapItems.forEach((item, i) => {
      fitText(
        this.add
          .text(
            W / 2,
            recapY + 22 + i * 20,
            item,
            fontStyle(FONT_SIZE.CAPTION, "#888888"),
          )
          .setOrigin(0.5),
        maxW,
      );
    });

    // ── NEW EPOCH button ───────────────────────────────────────
    const btnY = 500 + shift;
    const btnW = Math.min(320, W - 2 * EDGE_MARGIN);
    const drawBtn = (color) => {
      btnBg.clear();
      btnBg.fillStyle(color);
      btnBg.fillRoundedRect(W / 2 - btnW / 2, btnY - 16, btnW, 40, 8);
    };
    const btnBg = this.add.graphics();
    drawBtn(0xffd700);

    const btnText = fitText(
      this.add
        .text(
          W / 2,
          btnY + 4,
          "NEW EPOCH STARTS NOW",
          fontStyle(FONT_SIZE.SUBHEADING, "#0a0a0a"),
        )
        .setOrigin(0.5),
      btnW - EDGE_MARGIN,
    ).setInteractive({ useHandCursor: true });

    btnText.on("pointerdown", () => {
      this.scene.start("MenuScene");
    });

    btnText.on("pointerover", () => drawBtn(0xffed4a));
    btnText.on("pointerout", () => drawBtn(0xffd700));

    // Button pulse
    this.tweens.add({
      targets: [btnText],
      scaleX: { from: 1, to: 1.03 },
      scaleY: { from: 1, to: 1.03 },
      duration: 1000,
      yoyo: true,
      repeat: -1,
    });

    // ESC to skip
    this.input.keyboard.on("keydown-ESC", () => this.scene.start("MenuScene"));

    addMuteButton(this);

    // Sound
    SoundManager.playBossFanfare();
  }

  /**
   * Draw the three vesting bars from `top`, each with its label centred
   * above the bar (never straddling the fill boundary). Bars sit side by
   * side on wide canvases and stack vertically on narrow ones.
   * Returns the y just below the last bar.
   */
  _drawVesting(vestData, top) {
    const W = this.scale.width;
    const narrow = isNarrow(this);
    const count = vestData.length;
    const barW = narrow
      ? W - 4 * EDGE_MARGIN
      : Math.min(160, (W - 2 * EDGE_MARGIN - (count - 1) * VEST_GAP) / count);
    const rowW = narrow ? barW : count * barW + (count - 1) * VEST_GAP;
    const startX = W / 2 - rowW / 2;
    let bottom = top;

    vestData.forEach((v, i) => {
      const label = fitText(
        this.add
          .text(
            0,
            0,
            `${v.label} — ${v.pct}`,
            fontStyle(FONT_SIZE.CAPTION, "#FFFFFF"),
          )
          .setOrigin(0.5, 0),
        barW,
      );
      const rowH = label.height + VEST_LABEL_GAP + VEST_BAR_H;
      const x = narrow ? startX : startX + i * (barW + VEST_GAP);
      const y = narrow ? top + i * (rowH + VEST_GAP / 2) : top;
      label.setPosition(x + barW / 2, y);
      const barY = y + label.height + VEST_LABEL_GAP;

      // Bar background
      const bar = this.add.graphics();
      bar.fillStyle(0x333333);
      bar.fillRect(x, barY, barW, VEST_BAR_H);

      // Bar fill
      const fillPct = parseInt(v.pct, 10) / 100;
      const fillColor = parseInt(v.color.replace("#", ""), 16);
      bar.fillStyle(fillColor, 0.8);
      bar.fillRect(x, barY, barW * fillPct, VEST_BAR_H);

      bottom = Math.max(bottom, barY + VEST_BAR_H);
    });

    return bottom;
  }

  _startGoldRain() {
    // Simple gold particle rain using Phaser graphics
    const particles = [];
    const W = this.scale.width;
    const H = this.scale.height;

    for (let i = 0; i < 30; i++) {
      const x = Math.random() * W;
      const y = Math.random() * -H;
      const size = 2 + Math.random() * 4;
      const speed = 40 + Math.random() * 80;
      const gfx = this.add.graphics();
      gfx.fillStyle(0xffd700, 0.3 + Math.random() * 0.5);
      gfx.fillRect(0, 0, size, size);
      gfx.setPosition(x, y);
      particles.push({ gfx, speed, x, y: gfx.y });
    }

    this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        particles.forEach((p) => {
          p.gfx.y += p.speed * 0.016;
          if (p.gfx.y > H + 20) {
            p.gfx.y = -10;
            p.gfx.x = Math.random() * W;
          }
        });
      },
    });
  }

  async _loadPlayerRank(handle, baseY) {
    const W = this.scale.width;
    const maxW = W - EDGE_MARGIN;

    try {
      const epochInfo = await PaymentManager.getEpochInfo();
      if (!epochInfo || !epochInfo.epoch) return;

      const playerRank = epochInfo.playerRank || "?";
      const playerScore = (epochInfo.playerScore || 0).toLocaleString();
      const totalPlayers = epochInfo.epoch.totalPlayers || "?";

      fitText(
        this.add
          .text(
            W / 2,
            baseY,
            "YOUR RESULT",
            fontStyle(FONT_SIZE.BODY, "#00FFFF"),
          )
          .setOrigin(0.5),
        maxW,
      );

      fitText(
        this.add
          .text(
            W / 2,
            baseY + 22,
            `RANK #${playerRank}`,
            fontStyle(FONT_SIZE.HEADING, "#39FF14"),
          )
          .setOrigin(0.5),
        maxW,
      );

      fitText(
        this.add
          .text(
            W / 2,
            baseY + 48,
            `${playerScore} PTS`,
            fontStyle(FONT_SIZE.BODY, "#F5E6C8"),
          )
          .setOrigin(0.5),
        maxW,
      );

      // Percentile
      if (typeof playerRank === "number" && typeof totalPlayers === "number") {
        const percentile = Math.max(
          1,
          Math.round((1 - playerRank / totalPlayers) * 100),
        );
        fitText(
          this.add
            .text(
              W / 2,
              baseY + 68,
              `TOP ${percentile}%`,
              fontStyle(FONT_SIZE.SUBHEADING, "#FF00FF"),
            )
            .setOrigin(0.5),
          maxW,
        );
      }
    } catch {
      fitText(
        this.add
          .text(
            W / 2,
            baseY + 20,
            "COULD NOT LOAD RANK",
            fontStyle(FONT_SIZE.CAPTION, "#999999"),
          )
          .setOrigin(0.5),
        maxW,
      );
    }
  }
}

export default EpochEndScene;
